import React, { useState, useEffect } from "react";
import { FaRegSave } from "react-icons/fa";
import { TiPencil } from "react-icons/ti";
import { Link } from "react-router-dom";
import Pricing from "./Pricing";

const EditPriceLists = () => {
  const [percentage, setPercentage] = useState("");
  const [textInput, setTextInput] = useState("");
  const [selectedProcedure, setSelectedProcedure] = useState(null);
  const [procedures, setProcedures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editablePrices, setEditablePrices] = useState({});
  const [saving, setSaving] = useState(false);
  const [userLabId, setUserLabId] = useState(null);
  const [isLabIdChecked, setIsLabIdChecked] = useState(false);

  // Get userLabId from localStorage on component mount
  useEffect(() => {
    const storedUserLabId = localStorage.getItem('userLabId');
    console.log("Retrieved userLabId from localStorage:", storedUserLabId);
    setUserLabId(storedUserLabId);
    setIsLabIdChecked(true); // Mark that we've checked
  }, []);

  // Fetch procedures from API based on userLabId
  useEffect(() => {
    const fetchProcedures = async () => {
      if (!isLabIdChecked) {
        // Haven't checked localStorage yet
        return;
      }

      setLoading(true);
      setError("");
      
      try {
        console.log("Fetching procedures... UserLabId:", userLabId);
        
        let apiUrl;
        
        // If userLabId exists, use lab-specific endpoint
        if (userLabId) {
          apiUrl = `https://hms.backend.hiragroupcloud.com/api/procedures/lab/${userLabId}`;
          console.log("Using lab-specific endpoint:", apiUrl);
        } else {
          // If no userLabId, fetch all procedures
          apiUrl = "https://hms.backend.hiragroupcloud.com/api/procedures";
          console.log("No userLabId found, fetching all procedures");
        }
        
        let response = await fetch(apiUrl);
        
        // If lab-specific endpoint returns 404, fall back to general endpoint
        if (response.status === 404 && userLabId) {
          console.log("Lab-specific endpoint returned 404, trying general endpoint...");
          response = await fetch("https://hms.backend.hiragroupcloud.com/api/procedures");
          
          if (!response.ok) {
            throw new Error(`Failed to fetch procedures: ${response.status}`);
          }
          
          const data = await response.json();
          const allProcedures = data.data || data;
          
          // Filter client-side by userLabId
          console.log("Filtering procedures client-side for lab ID:", userLabId);
          const filteredProcedures = allProcedures.filter(
            procedure => procedure.addedBy?.userLabId === userLabId
          );
          
          console.log(`Found ${filteredProcedures.length} procedures for lab ${userLabId}`);
          setProcedures(filteredProcedures);
          initializeEditablePrices(filteredProcedures);
          
        } else if (!response.ok) {
          throw new Error(`Failed to fetch procedures: ${response.status}`);
        } else {
          // API call successful
          const data = await response.json();
          const proceduresData = data.data || data;
          
          // If we used general endpoint and have userLabId, filter client-side
          let finalProcedures = proceduresData;
          if (userLabId && apiUrl === "https://hms.backend.hiragroupcloud.com/api/procedures") {
            console.log("Filtering general endpoint results for lab ID:", userLabId);
            finalProcedures = proceduresData.filter(
              procedure => procedure.addedBy?.userLabId === userLabId
            );
          }
          
          console.log(`Fetched ${finalProcedures.length} procedures`);
          setProcedures(finalProcedures);
          initializeEditablePrices(finalProcedures);
        }
        
      } catch (err) {
        console.error("Error fetching procedures:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchProcedures();
  }, [userLabId, isLabIdChecked]); // Re-fetch when userLabId changes or when we've checked localStorage

  // Helper function to initialize editable prices
  const initializeEditablePrices = (proceduresData) => {
    const initialEditablePrices = {};
    proceduresData.forEach(procedure => {
      procedure.prices?.forEach(price => {
        const key = getPriceKey(procedure._id, price);
        initialEditablePrices[key] = price.price ? price.price.toString() : "0.00";
      });
    });
    setEditablePrices(initialEditablePrices);
  };

  // Helper function to generate unique key for each price
  const getPriceKey = (procedureId, price) => {
    if (price.pricingFor === "Group") {
      return `${procedureId}_Group_${price.target || 'default'}`;
    } else if (price.pricingFor === "Single Account") {
      return `${procedureId}_Single Account_${price.accountName || price.target || 'default'}`;
    } else {
      return `${procedureId}_${price.pricingFor}_${price.target || 'default'}`;
    }
  };

  const handleAlterPrices = () => {
    if (percentage) {
      // Apply percentage change to all editable prices
      const updatedPrices = { ...editablePrices };
      Object.keys(updatedPrices).forEach(key => {
        const currentValue = parseFloat(updatedPrices[key]);
        if (!isNaN(currentValue)) {
          const changePercentage = parseFloat(percentage) / 100;
          const newValue = currentValue * (1 + changePercentage);
          updatedPrices[key] = newValue.toFixed(2);
        }
      });
      setEditablePrices(updatedPrices);
      alert(`Prices altered by ${percentage}%${textInput ? ` with note: ${textInput}` : ''}`);
    } else {
      alert("Please select a percentage to alter prices");
    }
  };

  const handleProcedureClick = (procedure) => {
    sessionStorage.setItem('procedureId', procedure._id);
    sessionStorage.setItem('procedureName', procedure.name);
    setSelectedProcedure(procedure);
  };

  const handleBackToPriceLists = () => {
    setSelectedProcedure(null);
    sessionStorage.removeItem('procedureId');
    sessionStorage.removeItem('procedureName');
  };

  // Handle price input change
  const handlePriceChange = (procedureId, price, value) => {
    const key = getPriceKey(procedureId, price);
    setEditablePrices(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // Get group name based on pricing type
  const getGroupName = (price) => {
    if (price.pricingFor === "Group") {
      return price.target || "";
    }
    return "";
  };

  // Get account name based on pricing type
  const getAccountName = (price) => {
    if (price.pricingFor === "Single Account") {
      return price.accountName || price.target || "";
    }
    return "";
  };

  // Save all prices to backend using universal API
  const handleSaveAllPrices = async () => {
    setSaving(true);
    try {
      const updates = [];

      // Collect all price updates for both Groups and Single Accounts
      procedures.forEach(procedure => {
        procedure.prices?.forEach(price => {
          const key = getPriceKey(procedure._id, price);
          const newPrice = editablePrices[key];

          if (newPrice && !isNaN(parseFloat(newPrice))) {
            const originalPrice = price.price || 0;
            const updatedPrice = parseFloat(newPrice);

            if (updatedPrice !== originalPrice) {
              const targetName = price.pricingFor === "Group" ? price.target : (price.accountName || price.target);

              updates.push({
                procedureId: procedure._id,
                pricingType: price.pricingFor,
                target: targetName || "default",
                price: updatedPrice
              });
            }
          }
        });
      });

      if (updates.length === 0) {
        alert("No changes to save");
        setSaving(false);
        return;
      }

      const response = await fetch("https://hms.backend.hiragroupcloud.com/api/prices/update-prices", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ updates }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to save prices");
      }

      const result = await response.json();
      alert(`Successfully updated ${result.updated} prices`);

      // Refresh procedures
      await refreshProcedures();

    } catch (err) {
      console.error("Error saving prices:", err);
      alert("Failed to save prices: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  // Function to refresh procedures
  const refreshProcedures = async () => {
    setLoading(true);
    try {
      let apiUrl;
      
      if (userLabId) {
        apiUrl = `https://hms.backend.hiragroupcloud.com/api/procedures/lab/${userLabId}`;
      } else {
        apiUrl = "https://hms.backend.hiragroupcloud.com/api/procedures";
      }
      
      let response = await fetch(apiUrl);
      
      // If lab-specific endpoint returns 404, fall back to general endpoint
      if (response.status === 404 && userLabId) {
        response = await fetch("https://hms.backend.hiragroupcloud.com/api/procedures");
      }
      
      if (response.ok) {
        const data = await response.json();
        const proceduresData = data.data || data;
        
        // If we used general endpoint and have userLabId, filter client-side
        let finalProcedures = proceduresData;
        if (userLabId && apiUrl === "https://hms.backend.hiragroupcloud.com/api/procedures") {
          finalProcedures = proceduresData.filter(
            procedure => procedure.addedBy?.userLabId === userLabId
          );
        }
        
        setProcedures(finalProcedures);
        initializeEditablePrices(finalProcedures);
      }
    } catch (err) {
      console.error("Error refreshing procedures:", err);
    } finally {
      setLoading(false);
    }
  };

  // If a procedure is selected, show the Pricing component
  if (selectedProcedure) {
    return (
      <>
        <div style={{ width: "95%", margin: "0 auto" }}>
          <button
            onClick={handleBackToPriceLists}
            style={{
              backgroundColor: "#004aad",
              color: "white",
              border: "none",
              padding: "10px 20px",
              borderRadius: "4px",
              cursor: "pointer",
              marginBottom: "20px",
              display: "flex",
              alignItems: "center",
              gap: "5px"
            }}
          >
            ← Back to Price Lists
          </button>
          <div style={{
            backgroundColor: "white",
            padding: "20px",
            borderRadius: "8px",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
          }}>
            <h2 style={{ color: "#004aad", marginBottom: "20px" }}>
              Pricing for: {selectedProcedure.name}
            </h2>
            <Pricing />
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div style={{ width: "1600px", margin: "0 auto" }}>
        <div className="row">
          {/* Main Content */}
          <div className="col-md-12">
            <h1 style={{ marginBottom: "10px" }}>Edit Price Lists</h1>
            {/* Tools Section */}
            <h2 style={{ color: "#004aad", marginBottom: "10px" }}>Edit Tools</h2>
            <div
              style={{
                backgroundColor: "#f8f9fa",
                padding: "15px",
                border: "1px solid #ccc",
                borderTop: "2px solid #004aad",
                marginBottom: "20px",
                display: "flex",
                alignItems: "center",
                gap: "10px",
                flexWrap: "wrap"
              }}
            >
              <label style={{ fontWeight: "500" }}>Alter Prices By</label>
              <input
                type="text"
                value={textInput}
                onChange={(e) => setTextInput(e.target.value)}
                placeholder="Optional note"
                style={{
                  padding: "6px",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  width: "120px",
                }}
              />
              <select
                value={percentage}
                onChange={(e) => setPercentage(e.target.value)}
                style={{
                  padding: "6px",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  width: "80px",
                }}
              >
                <option value="">%</option>
                <option value="5">5%</option>
                <option value="10">10%</option>
                <option value="15">15%</option>
                <option value="20">20%</option>
                <option value="-5">-5%</option>
                <option value="-10">-10%</option>
                <option value="-15">-15%</option>
                <option value="-20">-20%</option>
              </select>
              <button
                onClick={handleAlterPrices}
                style={{
                  backgroundColor: "#004aad",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  padding: "8px 15px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "5px",
                }}
              >
                <TiPencil /> Alter Prices
              </button>
            </div>

            {/* Loading and Error States */}
            {loading && (
              <div style={{ textAlign: "center", padding: "20px" }}>
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p>Loading procedures...</p>
              </div>
            )}

            {error && (
              <div style={{ 
                color: "#721c24", 
                backgroundColor: "#f8d7da",
                border: "1px solid #f5c6cb",
                borderRadius: "4px",
                padding: "15px",
                marginBottom: "20px"
              }}>
                <strong>Error:</strong> {error}
                <div style={{ marginTop: "10px" }}>
                  <button 
                    onClick={() => window.location.reload()}
                    style={{
                      backgroundColor: "#dc3545",
                      color: "white",
                      border: "none",
                      padding: "5px 10px",
                      borderRadius: "4px",
                      cursor: "pointer"
                    }}
                  >
                    Retry
                  </button>
                </div>
              </div>
            )}

            {/* Price List Sections */}
            {!loading && !error && (
              <>
                <h3 style={{ color: "#004aad", marginBottom: "10px" }}>
                  Price Lists By Procedure
                  {procedures.length > 0 && (
                    <span style={{ 
                      fontSize: "14px", 
                      fontWeight: "normal",
                      marginLeft: "10px",
                      color: "#666"
                    }}>
                      ({procedures.length} procedures found)
                    </span>
                  )}
                </h3>

                {procedures.length === 0 ? (
                  <div style={{ 
                    textAlign: "center", 
                    padding: "40px",
                    backgroundColor: "#f8f9fa",
                    borderRadius: "4px",
                    border: "1px dashed #ccc",
                    marginBottom: "20px"
                  }}>
                    <h5>No procedures found</h5>
                    {userLabId ? (
                      <>
                        <p style={{ color: "#666", fontSize: "14px" }}>
                          No procedures are associated with your lab ID: <strong>{userLabId}</strong>
                        </p>
                        <p style={{ color: "#999", fontSize: "12px", marginTop: "10px" }}>
                          This could mean:
                          <br />1. Your lab hasn't created any procedures yet
                          <br />2. The procedures are associated with a different lab ID
                        </p>
                      </>
                    ) : (
                      <p style={{ color: "#666", fontSize: "14px" }}>
                        No procedures available in the system.
                      </p>
                    )}
                  </div>
                ) : (
                  procedures.map((procedure, index) => (
                    <div key={procedure._id} style={{ marginBottom: "25px" }}>
                      {/* Procedure Header - Clickable */}
                      <div
                        style={{
                          backgroundColor: "#f4f4f4",
                          padding: "10px",
                          fontWeight: "bold",
                          color: "#004aad",
                          borderTop: index === 0 ? "2px solid #004aad" : "1px solid #ccc",
                          cursor: "pointer",
                          transition: "all 0.3s",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center"
                        }}
                        onClick={() => handleProcedureClick(procedure)}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = "#e8e8e8";
                          e.currentTarget.style.color = "black";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = "#f4f4f4";
                          e.currentTarget.style.color = "#004aad";
                        }}
                      >
                        <span>
                          {procedure.name}
                          {procedure.category && (
                            <span style={{ 
                              fontSize: "12px", 
                              fontWeight: "normal",
                              marginLeft: "10px",
                              color: "#666"
                            }}>
                              ({procedure.category})
                            </span>
                          )}
                        </span>
                        {procedure.addedBy?.userLabId && (
                          <span style={{ 
                            fontSize: "11px", 
                            fontWeight: "normal",
                            color: userLabId === procedure.addedBy.userLabId ? "#28a745" : "#6c757d",
                            backgroundColor: userLabId === procedure.addedBy.userLabId ? "#d4edda" : "#e9ecef",
                            padding: "2px 6px",
                            borderRadius: "3px"
                          }}>
                            Lab: {procedure.addedBy.userLabId}
                            {userLabId === procedure.addedBy.userLabId && " ✓"}
                          </span>
                        )}
                      </div>

                      {/* Column Header */}
                      <div
                        style={{
                          backgroundColor: "#f4f4f4",
                          display: "flex",
                          fontWeight: "bold",
                          fontSize: "14px",
                          padding: "8px 10px",
                        }}
                      >
                        <div style={{ width: "25%" }}>Group</div>
                        <div style={{ width: "25%" }}>Account</div>
                        <div style={{ width: "25%" }}>Attributes</div>
                        <div style={{ width: "25%", textAlign: "right" }}>New Price</div>
                      </div>

                      {/* Price Rows */}
                      {procedure.prices && procedure.prices.length > 0 ? (
                        procedure.prices.map((price, priceIndex) => {
                          const key = getPriceKey(procedure._id, price);
                          const currentPrice = editablePrices[key] || (price.price ? price.price.toString() : "0.00");
                          const groupName = getGroupName(price);
                          const accountName = getAccountName(price);

                          return (
                            <div
                              key={price._id || priceIndex}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                padding: "10px 10px",
                                borderBottom: priceIndex === procedure.prices.length - 1
                                  ? "none"
                                  : "1px solid #eee",
                                fontSize: "14px",
                              }}
                            >
                              <div style={{ width: "25%" }}>
                                {groupName || "-"}
                              </div>
                              <div style={{ width: "25%" }}>
                                {accountName || "-"}
                              </div>
                              <div style={{ width: "25%" }}>
                                <span style={{ 
                                  fontSize: "12px", 
                                  color: "#666",
                                  backgroundColor: "#f0f0f0",
                                  padding: "2px 6px",
                                  borderRadius: "3px"
                                }}>
                                  {price.pricingFor}
                                </span>
                              </div>
                              <div style={{ width: "25%", textAlign: "right" }}>
                                <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: "5px" }}>
                                  <span style={{ fontSize: "12px", color: "#666" }}>$</span>
                                  <input
                                    type="text"
                                    value={currentPrice}
                                    onChange={(e) => handlePriceChange(
                                      procedure._id,
                                      price,
                                      e.target.value
                                    )}
                                    style={{
                                      width: "80px",
                                      textAlign: "right",
                                      border: "1px solid #ccc",
                                      borderRadius: "4px",
                                      padding: "3px 5px",
                                    }}
                                  />
                                </div>
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            padding: "10px 10px",
                            fontSize: "14px",
                            color: "#666",
                          }}
                        >
                          <div style={{ width: "100%", textAlign: "center" }}>
                            No prices configured for this procedure
                          </div>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </>
            )}

            {/* Save Button - Only show if there are procedures */}
            {!loading && !error && procedures.length > 0 && (
              <div style={{ 
                clear: "both", 
                paddingTop: "20px",
                borderTop: "1px solid #eee",
                marginTop: "20px"
              }}>
                <button
                  onClick={handleSaveAllPrices}
                  disabled={saving}
                  style={{
                    backgroundColor: saving ? "#6c757d" : "#004aad",
                    color: "white",
                    padding: "10px 20px",
                    border: "none",
                    borderRadius: "4px",
                    cursor: saving ? "not-allowed" : "pointer",
                    float: "right",
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    opacity: saving ? 0.6 : 1,
                  }}
                >
                  <FaRegSave /> {saving ? "Saving..." : "Save All Changes"}
                </button>
                <div style={{ clear: "both" }}></div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default EditPriceLists;